/* ============================================
   Native Possum — np-mobile-menu.js
   Mobile/tablet accordion menu: hamburger open/close (all .mobile-menu-toggle),
   accordion groups (data-accordion-group), swipe to close, menu/bag mutual
   exclusion, and the currency drawer. Focus, inert, Escape and aria-expanded
   are handled by npLayer (menu and currency drawer are stacked layers).
   Depends on: np-utilities.js (npLayer, npSwitchCurrency).
   Gotcha: selectCurrency sets sessionStorage 'np-reopen-bag' so np-bag-drawer
   reopens the bag after the currency reload.
   ============================================ */

(function() {
  'use strict';

  // ============================================
  // ELEMENTS
  // ============================================
  var menuToggles = document.querySelectorAll('.mobile-menu-toggle');
  var menu = document.getElementById('npMobileMenu');
  var menuBackdrop = document.getElementById('npMobileMenuBackdrop');
  var tabletCloseBtn = menu ? menu.querySelector('.np-mobile-menu__tablet-close') : null;
  var allAccordionTriggers = document.querySelectorAll('[data-accordion-target]');
  
  // State
  var isOpen = false;
  var scrollPosition = 0;
  var scrollLockMode = null;   // 'fixed' (mobile) or 'overflow' (tablet), set on open
  var addedSearchOpen = false; // true when openMenu added .search-open to the mobile header

  // ============================================
  // UTILITY: Lock/Unlock Body Scroll
  // ============================================
  function lockBodyScroll() {
    scrollPosition = window.scrollY;
    document.body.classList.add('np-mobile-menu-open');
    document.body.style.top = '-' + scrollPosition + 'px';
  }

  function unlockBodyScroll() {
    document.body.classList.remove('np-mobile-menu-open');
    document.body.style.top = '';
    window.scrollTo(0, scrollPosition);
  }

  // ============================================
  // OPEN MENU
  // ============================================
  function openMenu(trigger) {
    if (!menu || isOpen) return;
    
    isOpen = true;
    
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    
    menuToggles.forEach(function(toggle) {
      toggle.classList.add('is-menu-open');
      toggle.setAttribute('aria-label', 'Close menu');
    });
    
    // Scroll lock: mobile uses position:fixed, tablet uses overflow:hidden.
    // Remember which one so close undoes the same one even after a resize/rotate.
    scrollLockMode = window.innerWidth <= 767 ? 'fixed' : 'overflow';
    if (scrollLockMode === 'fixed') {
      lockBodyScroll();
    } else {
      document.body.style.overflow = 'hidden';
    }
    
    // Show backdrop (works at all sizes, CSS controls visibility per breakpoint)
    if (menuBackdrop) menuBackdrop.classList.add('active');
    
    // Force solid header (transparent homepage)
    var hw = document.getElementById('mobileHeaderWrapper');
    if (hw && !hw.classList.contains('search-open')) {
      hw.classList.add('search-open');
      addedSearchOpen = true;
    }
    var tw = document.getElementById('tabletHeaderWrapper');
    if (tw && tw.classList.contains('tablet-header--transparent')) tw.classList.add('is-scrolled');

    // v8: Close bag drawer if open (delayed so menu slides over visually)
    var bagDrawerEl = document.getElementById('bagDrawer');
    if (bagDrawerEl && bagDrawerEl.classList.contains('active') && window.bagDrawer) {
      setTimeout(function() {
        window.bagDrawer.close();
      }, 150);
    }

    // Close search if open
    var searchPanel = document.getElementById('mobileSearchExpanded');
    var searchToggle = document.getElementById('mobileSearchToggle');
    if (searchPanel && searchPanel.classList.contains('is-open')) {
      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
    }

    // Tablet (768-990): the menu covers the hamburger and has its own close
    // button, so it is a true modal. Mobile: the menu sits under the header and
    // the hamburger (now an X) is the only close control, so the header stays
    // usable and the menu is not aria-modal.
    var hasOwnClose = !!(tabletCloseBtn && tabletCloseBtn.getClientRects().length);
    if (hasOwnClose) {
      menu.setAttribute('aria-modal', 'true');
    } else {
      menu.removeAttribute('aria-modal');
    }
    window.npLayer.open(menu, {
      trigger: trigger,
      keep: hasOwnClose ? [menuBackdrop] : [hw, menuBackdrop],
      initialFocus: '.np-mobile-menu__item, .np-mobile-menu__secondary-header',
      onRequestClose: closeMenu
    });
  }

  // ============================================
  // CLOSE MENU
  // ============================================
  function closeMenu() {
    if (!menu || !isOpen) return;
    
    isOpen = false;
    
    // A currency drawer stacked on the menu goes with it
    closeCurrencyDrawer();
    
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    
    menuToggles.forEach(function(toggle) {
      toggle.classList.remove('is-menu-open');
      toggle.setAttribute('aria-label', 'Open menu');
    });
    
    // Close all accordions
    closeAllAccordions();
    
    // Unlock scroll — undo the mode that was applied on open
    if (scrollLockMode === 'fixed') {
      unlockBodyScroll();
    } else if (scrollLockMode === 'overflow') {
      document.body.style.overflow = '';
    }
    scrollLockMode = null;
    
    // Hide backdrop
    if (menuBackdrop) menuBackdrop.classList.remove('active');
    
    // Close search if open
    var searchPanel = document.getElementById('mobileSearchExpanded');
    var searchToggle = document.getElementById('mobileSearchToggle');
    if (searchPanel && searchPanel.classList.contains('is-open')) {
      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
    }

    // Undo the solid mobile header forced on open (np-header.js owns it otherwise)
    var hw = document.getElementById('mobileHeaderWrapper');
    if (hw && addedSearchOpen) hw.classList.remove('search-open');
    addedSearchOpen = false;

    // Restore tablet header if not scrolled
    var tw = document.getElementById('tabletHeaderWrapper');
    if (tw && tw.classList.contains('tablet-header--transparent') && window.scrollY <= 50) {
      tw.classList.remove('is-scrolled');
    }

    // Focus returns to the hamburger that opened it (npLayer)
    window.npLayer.close(menu);
  }

  // ============================================
  // TOGGLE MENU
  // ============================================
  function toggleMenu(e) {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu(e && e.currentTarget);
    }
  }

  // ============================================
  // ACCORDION
  // ============================================
  // Custom smooth scroll with ease-out curve (300ms)
  function smoothScrollTo(container, targetScrollTop, duration) {
    var start = container.scrollTop;
    var distance = targetScrollTop - start;
    if (Math.abs(distance) < 2) return; // Skip if already there
    var startTime = null;
    
    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic: decelerates toward end
      var eased = 1 - Math.pow(1 - progress, 3);
      container.scrollTop = start + (distance * eased);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }
    
    requestAnimationFrame(step);
  }

  function toggleAccordion(trigger) {
    var targetId = trigger.getAttribute('data-accordion-target');
    var accordion = document.getElementById(targetId);
    if (!accordion) return;
    
    var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      // Close this accordion — collapse in place, no scroll
      trigger.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
      accordion.classList.remove('is-open');
      accordion.setAttribute('aria-hidden', 'true');
    } else {
      // Close ALL other open accordions across all groups (instant, no transition)
      allAccordionTriggers.forEach(function(otherTrigger) {
        if (otherTrigger !== trigger) {
          var otherId = otherTrigger.getAttribute('data-accordion-target');
          var otherAccordion = document.getElementById(otherId);
          if (otherAccordion && otherTrigger.getAttribute('aria-expanded') === 'true') {
            otherAccordion.style.transition = 'none';
            otherTrigger.classList.remove('is-expanded');
            otherTrigger.setAttribute('aria-expanded', 'false');
            otherAccordion.classList.remove('is-open');
            otherAccordion.setAttribute('aria-hidden', 'true');
            requestAnimationFrame(function() {
              otherAccordion.style.transition = '';
            });
          }
        }
      });
      
      // Open this accordion — 200ms expand + simultaneous eased scroll
      accordion.style.transition = 'max-height 0.2s ease';
      trigger.classList.add('is-expanded');
      trigger.setAttribute('aria-expanded', 'true');
      accordion.classList.add('is-open');
      accordion.setAttribute('aria-hidden', 'false');
      
      // Delay scroll slightly so layout recalculates with new content height
      setTimeout(function() {
        var scrollContainer = menu.querySelector('.np-mobile-menu__content');
        if (!scrollContainer) return;
        
        var containerRect = scrollContainer.getBoundingClientRect();
        var accordionRect = accordion.getBoundingClientRect();
        var triggerRect = trigger.getBoundingClientRect();
        
        // Check if the bottom of the accordion is cut off
        var bottomVisible = accordionRect.bottom <= containerRect.bottom;
        var triggerVisible = triggerRect.top >= containerRect.top;
        
        if (bottomVisible && triggerVisible) {
          // Everything fits — don't scroll
          return;
        }
        
        // Scroll trigger to 8px from top of container
        var offset = triggerRect.top - containerRect.top;
        var scrollTarget = Math.max(0, scrollContainer.scrollTop + offset - 8);
        smoothScrollTo(scrollContainer, scrollTarget, 300);
      }, 50);
    }
  }

  function closeAllAccordions() {
    allAccordionTriggers.forEach(function(trigger) {
      trigger.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
    });
    
    document.querySelectorAll('.np-mobile-menu__accordion').forEach(function(accordion) {
      accordion.classList.remove('is-open');
      accordion.setAttribute('aria-hidden', 'true');
    });
  }

  // ============================================
  // CURRENCY DRAWER
  // ============================================
  var currencyDrawer = document.getElementById('npCurrencyDrawer');
  var currencyDrawerBackdrop = document.getElementById('npCurrencyDrawerBackdrop');
  var currencyDrawerOptions = document.querySelectorAll('.np-currency-drawer__option');
  var currencyDrawerInput = document.getElementById('npCurrencyDrawerInput');
  var currencyDrawerForm = document.getElementById('npCurrencyDrawerForm');
  
  var isCurrencyDrawerOpen = false;
  var currencyDrawerTouchStartY = 0;

  function openCurrencyDrawer(trigger) {
    if (!currencyDrawer || !currencyDrawerBackdrop || isCurrencyDrawerOpen) return;
    
    isCurrencyDrawerOpen = true;
    currencyDrawer.classList.add('is-open');
    currencyDrawer.setAttribute('aria-hidden', 'false');
    currencyDrawerBackdrop.classList.add('is-open');
    
    // Stacks over the mobile menu; backdrop stays tappable to close
    window.npLayer.open(currencyDrawer, {
      trigger: trigger,
      keep: [currencyDrawerBackdrop],
      initialFocus: '.np-currency-drawer__option',
      onRequestClose: closeCurrencyDrawer
    });
  }

  function closeCurrencyDrawer() {
    if (!currencyDrawer || !currencyDrawerBackdrop || !isCurrencyDrawerOpen) return;
    
    isCurrencyDrawerOpen = false;
    currencyDrawer.classList.remove('is-open');
    currencyDrawer.setAttribute('aria-hidden', 'true');
    currencyDrawerBackdrop.classList.remove('is-open');
    window.npLayer.close(currencyDrawer);
  }

  function selectCurrency(countryCode) {
    if (!currencyDrawerInput || !currencyDrawerForm) return;

    // v17: If bag drawer is currently open, flag it to reopen after reload
    var bagDrawerEl = document.getElementById('bagDrawer');
    if (bagDrawerEl && bagDrawerEl.classList.contains('active')) {
      try { sessionStorage.setItem('np-reopen-bag', '1'); } catch (e) {}
    }

    currencyDrawerInput.value = countryCode;
    window.npSwitchCurrency(countryCode, currencyDrawerForm);
  }

  // Delegated: this file runs while the header is parsed, before later
  // triggers exist (the bag drawer's globe button renders after the header).
  document.addEventListener('click', function(e) {
    var trigger = e.target && e.target.closest ? e.target.closest('[data-currency-drawer-trigger]') : null;
    if (!trigger) return;
    e.preventDefault();
    openCurrencyDrawer(trigger);
  });

  if (currencyDrawerBackdrop) {
    currencyDrawerBackdrop.addEventListener('click', closeCurrencyDrawer);
  }

  currencyDrawerOptions.forEach(function(option) {
    option.addEventListener('click', function() {
      var countryCode = option.getAttribute('data-country-code');
      selectCurrency(countryCode);
    });
  });

  if (currencyDrawer) {
    currencyDrawer.addEventListener('touchstart', function(e) {
      currencyDrawerTouchStartY = e.touches[0].clientY;
    }, { passive: true });

    currencyDrawer.addEventListener('touchend', function(e) {
      var touchEndY = e.changedTouches[0].clientY;
      var swipeDistance = touchEndY - currencyDrawerTouchStartY;
      if (swipeDistance > 50 && isCurrencyDrawerOpen) {
        closeCurrencyDrawer();
      }
    }, { passive: true });
  }

  // ============================================
  // SWIPE TO CLOSE (swipe left)
  // ============================================
  var touchStartX = 0;
  var touchEndX = 0;

  function handleTouchStart(e) {
    touchStartX = e.touches[0].clientX;
  }

  function handleTouchEnd(e) {
    touchEndX = e.changedTouches[0].clientX;
    var swipeDistance = touchStartX - touchEndX;
    if (swipeDistance > 100 && isOpen) {
      closeMenu();
    }
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================
  
  // Backdrop click closes menu (tablet)
  if (menuBackdrop) {
    menuBackdrop.addEventListener('click', closeMenu);
  }

  // Hamburger toggle — opens AND closes (all instances)
  menuToggles.forEach(function(toggle) {
    toggle.addEventListener('click', toggleMenu);
  });

  // Tablet close button
  if (tabletCloseBtn) {
    tabletCloseBtn.addEventListener('click', closeMenu);
  }
  
  // Accordion triggers
  allAccordionTriggers.forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      toggleAccordion(trigger);
    });
  });
  
  // Escape and focus containment: npLayer (np-utilities.js)
  
  // Swipe to close
  if (menu) {
    menu.addEventListener('touchstart', handleTouchStart, { passive: true });
    menu.addEventListener('touchend', handleTouchEnd, { passive: true });
  }

})();